<template>
  <div class="page-contact">
    <SectionsPageBanner heading="Contact" />

    <section class="contact-form">
      <div class="container">
        <div class="row">
          <div class="col-12">
            <ElementsHeadingsDefault heading="Send us a message" :sub-heading="subject === 'Advertise with New Homes' ? 'Advertise with New Homes' : 'Get in touch'" />
          </div>
          <div class="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2 __home-loans-get-in-touch-form-wrapper">
            <form v-if="!shouldShowResponse" class="contact-form__form-wrapper">
              <div class="row">
                <div class="col-6 col-md-4 form-control">
                  <ElementsInputsText v-model="name" placeholder="Name" @update:model-value="clearErrors('name')" />
                  <transition name="fade">
                    <span v-if="error.name" class="form-errors">
                      {{ error.name }}
                    </span>
                  </transition>
                </div>
                <div class="col-6 col-md-4 form-control">
                  <ElementsInputsText v-model="email" placeholder="Email" type="email" @update:model-value="clearErrors('email')" />
                  <transition name="fade">
                    <span v-if="error.email" class="form-errors">
                      {{ error.email }}
                    </span>
                  </transition>
                </div>
                <div class="col-12 col-md-4 form-control">
                  <ElementsInputsText v-model="subject" placeholder="Subject" @update:model-value="clearErrors('subject')" />
                  <transition name="fade">
                    <span v-if="error.subject" class="form-errors">
                      {{ error.subject }}
                    </span>
                  </transition>
                </div>
                <div class="col-12 form-control">
                  <ElementsInputsTextarea v-model="message" placeholder="Message" :rows="4" @update:model-value="clearErrors('message')" />
                  <transition name="fade" style="bottom: -48px">
                    <span v-if="error.message" class="form-errors">
                      {{ error.message }}
                    </span>
                  </transition>
                </div>

                <div class="col-12 __contact-action">
                  <ElementsButtonsRegular v-if="!responseError" label="Submit" :loading="isLoading" @click="sendMessage" />
                  <span v-else style="color: red;">
                    {{ responseError }}
                  </span>
                </div>
                <div class="col-md-12">
                  <ElementsRecaptcha ref="recaptchaRef" />
                </div>
              </div>
            </form>
            <div v-else class="contact-form__confirmation-wrapper">
              <div class="check-icon-wrapper">
                <img v-lazy-load  :data-src="checkIcon" alt="Check icon">
              </div>
              <div class="confirmation-text">
                <h3>Thank you for reaching out!</h3>
                <p>Thank you for reaching out! Our team will contact you soon.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import checkIcon from 'assets/images/icons/check.webp';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {useRoute} from '#app';

const route = useRoute();
const name: Ref<string> = ref('');
const email: Ref<string> = ref('');
const subject: Ref<string> = ref('');
const message: Ref<string> = ref('');
const shouldShowResponse: Ref<boolean> = ref(false);
const recaptchaRef: Ref<HTMLElement | null> = ref(null);
const config = useRuntimeConfig();
const nuxtApp = useNuxtApp();
const responseError: Ref<string> = ref('');
const isLoading: Ref<boolean> = ref(false);
const error: Ref<{name: string; email: string; subject: string; message: string}> = ref({
  name: '',
  email: '',
  subject: '',
  message: '',
});

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const title = route.query?.subject ? 'Advertise With Us | New Homes' : 'Contact Us | New Homes - Reach Out for Assistance and Information';
const description = route.query?.subject ? 'Reach a dedicated audience of homebuyers and investors on NewHomes.co.nz. Advertise with us and amplify your property\'s visibility. Contact us today!' : 'Got a question or need assistance? Reach out to the New Homes team. We\'re here to guide you on your home buying journey.';
useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
  twitterCard: 'summary_large_image',
  twitterTitle: title,
  twitterDescription: description,
  twitterImage: 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg',
});

onMounted(() => {
  if (route.query && route.query.subject) {
    subject.value = route.query.subject as string;
  }
});

/**
 * Clear errors
 *
 * @param field
 */
function clearErrors (field: 'name' | 'email' | 'subject' | 'message' | 'all') {
  if (field === 'all') {
    error.value = {
      name: '',
      email: '',
      subject: '',
      message: '',
    };
  } else {
    error.value[field] = '';
  }
}

/**
 * Send message
 */
async function sendMessage () {
  validateFields('all');
  if (error.value.name || error.value.email || error.value.subject || error.value.message) {
    return;
  }
  isLoading.value = true;
  if (recaptchaRef.value || config.public.appEnv === 'local') {
    let token = 'local';
    if (config.public.appEnv !== 'local') {
      token = await (recaptchaRef.value as any).validate();
    }

    const baseUrl = nuxtApp.$config.public.apiUrl;
    const response = await fetch(baseUrl + '/public/cms/contact', {
      method: 'POST',
      headers: globalApiHeaders(),
      body: JSON.stringify({
        name: name.value,
        email: email.value,
        subject: subject.value,
        message: message.value,
        token,
      }),
    });

    setTimeout(() => {
      isLoading.value = false;

      if (response.status === 200) {
        shouldShowResponse.value = true;
      } else {
        responseError.value = 'Oops, Sorry! Something went wrong. Refresh the page and please try again later.';
      }
    }, 800);
  }
}

/**
 * Validate fields
 *
 * @param field
 */
function validateFields (field: 'name' | 'email' | 'subject' | 'message' | 'all') {
  if (field === 'all' || field === 'name') {
    if (!name.value) {
      error.value.name = 'Name is required';
    }
    if (name.value.length < 3) {
      error.value.name = 'Name is too short';
    }
  }
  if (field === 'all' || field === 'email') {
    if (!email.value) {
      error.value.email = 'Please enter your email here to subscribe!';
    } else if (!(/.+@.+\..+/.test(email.value))) {
      error.value.email = 'Please enter a valid email address!';
    }
  }
  if (field === 'all' || field === 'subject') {
    if (!subject.value) {
      error.value.subject = 'Subject is required';
    }
  }
  if (field === 'all' || field === 'message') {
    if (!message.value) {
      error.value.message = 'Message is required';
    }
    if (message.value.length < 10) {
      error.value.message = 'Message is too short. Please add a meaningful message.';
    }
  }
}
</script>

<style lang="scss">
.page-contact {
  .contact-form__form-wrapper {
    .form-control {
      margin-bottom: 60px;
      position: relative;

      .el-input-text,
      .el-input-textarea {
        max-width: 100%;
        height: 50px;

        input,
        textarea {
          padding: 8px 4px;
          border-bottom: 1px solid #c2c2c2 !important;
          resize: none;
        }
      }

      .form-errors {
        position: absolute;
        bottom: -16px;
        left: 24px;
        height: 20px;
        color: #ff6d6d;
        font-size: 14px;
      }
    }

    .__contact-action {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .contact-form__confirmation-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 12px;

    .check-icon-wrapper {
      width: 30px;
      height: 30px;
      border-radius: 50%;
      background: rgba(82, 198, 184, 0.3);
      display: flex;
      justify-content: center;
      align-items: center;
      margin-right: 20px;

      img {
        width: 18px;
        height: 18px;
      }
    }

    .confirmation-text {
      h3 {
        color: rgb(var(--primary-dark));
        font-size: 18px;
        font-weight: 500;
        line-height: 1.2;
        margin-bottom: 0;
      }
    }
  }
}
</style>
